import React, { useState } from 'react';

import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';

import { rechercherPieceAddAction, fetchPieceGestionLignes } from 'utils/ts-queries';
import { ApiPieceAddAction, ApiPieceGestionLigne } from 'types/apiReturns';

type Props = {
    label?: string;
    // Impose le préfixe « L » (bon de livraison) — seul type de pièce que le back sait résoudre
    // en lignes et pour lequel le lien GesWeb « bonlivraison » est valide. Par défaut true.
    prefixeLObligatoire?: boolean;
    // Notifie le parent du résultat de validation : la pièce validée (ou null) et ses lignes.
    onPieceValidee: (piece: ApiPieceAddAction | null, lignes: ApiPieceGestionLigne[]) => void;
    disabled?: boolean;
};

// Champ de saisie d'une pièce avec recherche d'existence lancée à la loupe ou sur Entrée.
// Tant que la pièce n'est pas validée, onPieceValidee est appelé avec (null, []).
export default function PieceLookup({ label = 'Pièce à utiliser pour le retour', prefixeLObligatoire = true, onPieceValidee, disabled = false }: Props) {

    const [saisie, setSaisie] = useState('');
    const [verification, setVerification] = useState(false);
    const [erreur, setErreur] = useState<string | null>(null);
    // Vrai une fois qu'une pièce a été validée (affiche le bouton d'effacement).
    const [validee, setValidee] = useState(false);

    const messageErreur = 'Pièce erronée, veuillez ressaisir';

    const reinitialiser = () => {
        setSaisie('');
        setErreur(null);
        setValidee(false);
        onPieceValidee(null, []);
    };

    const rechercher = async () => {

        const numeroPiece = saisie.trim();

        if (numeroPiece === '') {
            setErreur(null);
            setValidee(false);
            onPieceValidee(null, []);
            return;
        }

        // Contrôle de préfixe (bon de livraison) : évite notamment les demandes de transfert.
        if (prefixeLObligatoire && !numeroPiece.toUpperCase().startsWith('L')) {
            setErreur(messageErreur);
            setValidee(false);
            onPieceValidee(null, []);
            return;
        }

        setVerification(true);
        setErreur(null);

        try {
            const piece = await rechercherPieceAddAction(numeroPiece);

            if (piece === null) {
                setErreur(messageErreur);
                setValidee(false);
                onPieceValidee(null, []);
                return;
            }

            const lignes = await fetchPieceGestionLignes(numeroPiece);
            setValidee(true);
            onPieceValidee(piece, lignes);
        }
        catch (error) {
            console.error('PieceLookup : ', error);
            setErreur(messageErreur);
            setValidee(false);
            onPieceValidee(null, []);
        }
        finally {
            setVerification(false);
        }
    };

    const renderEndAdornment = () => {
        if (verification) {
            return (
                <InputAdornment position="end">
                    <CircularProgress size={18} />
                </InputAdornment>
            );
        }

        // Une pièce validée : bouton d'effacement pour ressaisir.
        if (validee) {
            return (
                <InputAdornment position="end">
                    <IconButton aria-label="Effacer la pièce" size="small" onClick={reinitialiser} disabled={disabled}>
                        <ClearIcon fontSize="small" />
                    </IconButton>
                </InputAdornment>
            );
        }

        // Sinon : loupe pour lancer la recherche.
        return (
            <InputAdornment position="end">
                <IconButton aria-label="Rechercher la pièce" size="small" onClick={rechercher} disabled={disabled}>
                    <SearchIcon fontSize="small" />
                </IconButton>
            </InputAdornment>
        );
    };

    return (
        <TextField
            label={label}
            value={saisie}
            onChange={event => {
                setSaisie(event.target.value);
                // Toute modification invalide la validation précédente.
                if (erreur !== null) {
                    setErreur(null);
                }
                if (validee) {
                    setValidee(false);
                    onPieceValidee(null, []);
                }
            }}
            onKeyDown={event => {
                if (event.key === 'Enter') {
                    event.preventDefault();
                    rechercher();
                }
            }}
            required
            disabled={disabled || verification}
            error={erreur !== null}
            helperText={erreur ?? ''}
            fullWidth
            InputProps={{
                endAdornment: renderEndAdornment()
            }}
        />
    );
}
